import { ActionTree, Commit, MutationTree } from "vuex";
import { IProjectState, IRootState } from "@iloq/shared";
import { IDiscountInfo, IDiscounts } from "@iloq/shared/IDiscounts";
import ProjectApi, { DiscountNotAvailableCause } from "@/api/ProjectApi";
import { handleCommonApiErrors } from "@/api/CommonApiErrorHandler";
import i18n from "../i18n";

const projectApi = new ProjectApi();

const initialState: IProjectState = {
  loading: false,
  error: false,
};

const showProjectIdError = (key: string, projectDiscountCode: string, commit: Commit) => {
  commit(
    "showGeneralError",
    {
      dialogTitle: `${i18n.t("ordering.projectDetails.projectId.error.title")} — ${projectDiscountCode}`,
      dialogText: i18n.t(`ordering.projectDetails.projectId.error.description.${key}`),
    },
    { root: true },
  );
};

const actions: ActionTree<IRootState, IRootState> = {
  async fetchDiscountInfo(
    { commit },
    projectDiscountCode: string,
  ): Promise<IDiscountInfo | void | "blocked"> {
    try {
      commit("setDiscountsLoading", true);
      const discountInfo = await projectApi.fetchDiscountInfo(projectDiscountCode);

      commit("setDiscountsLoading", false);

      if (!discountInfo) {
        showProjectIdError("NOT_FOUND", projectDiscountCode, commit);
        return "blocked";
      }

      // Non-blocking discountNotAvailableCause
      if (
        "discountNotAvailableCause" in discountInfo &&
        discountInfo.discountNotAvailableCause === DiscountNotAvailableCause.NO_DISCOUNT_SET
      ) {
        return;
      }

      // Blocking discountNotAvailableCause
      if ("discountNotAvailableCause" in discountInfo) {
        showProjectIdError(discountInfo.discountNotAvailableCause, projectDiscountCode, commit);
        return "blocked";
      }

      return discountInfo;
    } catch (error) {
      handleCommonApiErrors(error as Error);
      commit("setDiscountsLoading", false);
      commit("setDiscountsError", true);
    }
  },
  async fetchDiscounts({ dispatch, getters }, projectDiscountCode?: string): Promise<IDiscounts> {
    const discounts: IDiscounts = {
      projectDiscountCode: projectDiscountCode,
    };

    if (projectDiscountCode) {
      const discountInfo: IDiscountInfo | undefined | "blocked" = await dispatch(
        "fetchDiscountInfo",
        projectDiscountCode,
      );

      if (discountInfo) {
        if (discountInfo === "blocked") {
          discounts.projectDiscountCode = undefined;
        } else {
          discounts.discount = discountInfo.basicDiscountPercentage;
          discounts.onlineDiscount = discountInfo.onlineProductDiscountPercentage;
          discounts.netPrices = discountInfo.netPricesForProducts;
          discounts.percentagePointDiscount = discountInfo.percentagePointDiscount;
          discounts.owner = discountInfo.projectOwner;
        }
      }
    }

    return discounts;
  },
};

const mutations: MutationTree<IProjectState> = {
  setDiscountsLoading(state: IProjectState, loading: boolean): void {
    state.error = false;
    state.loading = loading;
  },
  setDiscountsError(state: IProjectState, error: boolean): void {
    state.error = error;
  },
};

export const projectModule = {
  actions,
  mutations,
  state: initialState,
  namespaced: true,
};
